import { useEffect, useMemo, useState } from 'react';
import { Plus, Timer, Trash2 } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { Badge } from '@/components/Shared/Badge';
import { Button } from '@/components/Shared/Button';
import { Card } from '@/components/Shared/Card';
import { EmptyState } from '@/components/Shared/EmptyState';
import { FormField } from '@/components/Shared/FormField';
import { Loading } from '@/components/Shared/Loading';
import { SlideOver, SlideOverFooter } from '@/components/Shared/SlideOver';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/Shared/Table';
import { TableActions } from '@/components/Shared/TableActions';
import { formFieldInputClassName } from '@/components/Shared/formFieldStyles';
import { formatWindowHour } from '@/utils/dateUtils';
import { useTimeClockGroupStore, type TimeClockGroup } from '@/store/useTimeClockGroupStore';
import {
  GROUP_LIMITS,
  emptyGroupForm,
  formToRequest,
  groupFormsEqual,
  groupToForm,
  hasGroupErrors,
  newPeriod,
  validateGroupForm,
  type GroupErrors,
  type GroupForm,
  type PeriodForm,
} from './timeClockGroupForm';

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);
const START_HOURS = Array.from({ length: 24 }, (_, i) => i);
const END_HOURS = Array.from({ length: 24 }, (_, i) => i + 1);

/** Month names in the UI language, index 1–12. */
function useMonthNames(format: 'long' | 'short'): (month: number) => string {
  const { i18n } = useTranslation();
  return useMemo(() => {
    let formatter: Intl.DateTimeFormat;
    try {
      formatter = new Intl.DateTimeFormat(i18n.language, { month: format });
    } catch {
      formatter = new Intl.DateTimeFormat('de', { month: format });
    }
    const names = MONTHS.map((m) => formatter.format(new Date(2000, m - 1, 1)));
    return (month: number) => names[month - 1] ?? String(month);
  }, [i18n.language, format]);
}

/**
 * Time clock groups (FACF-149): seasonal clock-in windows per group of employees. Months no
 * period covers fall back to the tenant's time clock policy.
 */
export default function TimeClockGroupsPage() {
  const { t } = useTranslation('timeClockGroups');
  const { groups, loaded, loading, error, fetchGroups } = useTimeClockGroupStore();
  const shortMonth = useMonthNames('short');

  const [editing, setEditing] = useState<TimeClockGroup | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    void fetchGroups({ force: true });
  }, [fetchGroups]);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (group: TimeClockGroup) => {
    setEditing(group);
    setDialogOpen(true);
  };

  const periodSummary = (group: TimeClockGroup) =>
    (group.periods ?? []).map((p, i) => (
      <div key={i} className="tabular-nums">
        <span className="text-slate-900">
          {p.fromMonth === p.toMonth
            ? shortMonth(p.fromMonth ?? 1)
            : `${shortMonth(p.fromMonth ?? 1)}–${shortMonth(p.toMonth ?? 12)}`}
        </span>
        <span className="text-slate-500">
          {' · '}
          {formatWindowHour(p.stampingStartHour ?? 0)}–{formatWindowHour(p.stampingEndHour ?? 24)}
          {' · '}
          {t('list.countsFrom', { time: formatWindowHour(p.workCountStartHour ?? 0) })}
        </span>
      </div>
    ));

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
              {t('title')}
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">{t('subtitle')}</p>
          </div>
          <Button onClick={openCreate} leftIcon={<Plus className="w-3.5 h-3.5" strokeWidth={2} />}>
            {t('actions.create')}
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 space-y-1.5 text-[12px] text-slate-600">
        <p>{t('help.rule')}</p>
        <p>{t('help.fallback')}</p>
      </div>

      {loading && !loaded ? (
        <Loading />
      ) : error && !loaded ? (
        <Card>
          <EmptyState
            variant="error"
            title={t('errors.fetchFailed')}
            onRetry={() => void fetchGroups({ force: true })}
          />
        </Card>
      ) : groups.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Timer className="w-6 h-6" strokeWidth={2} />}
            title={t('empty.title')}
            description={t('empty.description')}
            action={
              <Button onClick={openCreate} leftIcon={<Plus className="w-3.5 h-3.5" strokeWidth={2} />}>
                {t('actions.create')}
              </Button>
            }
          />
        </Card>
      ) : (
        <Card noPadding>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>{t('fields.name')}</TableHeader>
                <TableHeader className="hidden sm:table-cell">{t('fields.periods')}</TableHeader>
                <TableHeader>{t('fields.status')}</TableHeader>
                <TableHeader className="text-right">{t('list.actions')}</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((group) => (
                <TableRow key={group.timeClockGroupId} onClick={() => openEdit(group)}>
                  <TableCell>
                    <div className="text-[13px] font-medium text-slate-900">{group.name}</div>
                    <div className="sm:hidden mt-1 text-[12px]">{periodSummary(group)}</div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-[12px]">
                    {periodSummary(group)}
                  </TableCell>
                  <TableCell>
                    {group.isActive ? (
                      <Badge variant="success">{t('status.active')}</Badge>
                    ) : (
                      <Badge variant="default">{t('status.inactive')}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
                      <TableActions onEdit={() => openEdit(group)} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <TimeClockGroupDialog
        isOpen={dialogOpen}
        group={editing}
        onClose={() => setDialogOpen(false)}
      />
    </div>
  );
}

function TimeClockGroupDialog({
  isOpen,
  group,
  onClose,
}: {
  isOpen: boolean;
  group: TimeClockGroup | null;
  onClose: () => void;
}) {
  const { t } = useTranslation(['timeClockGroups', 'common']);
  const tg = (key: string, options?: Record<string, unknown>) =>
    t(`timeClockGroups:${key}`, options ?? {});
  const { saving, createGroup, updateGroup } = useTimeClockGroupStore();
  const longMonth = useMonthNames('long');

  const [initial, setInitial] = useState<GroupForm>(emptyGroupForm);
  const [form, setForm] = useState<GroupForm>(initial);
  const [errors, setErrors] = useState<GroupErrors>({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const start = group ? groupToForm(group) : emptyGroupForm();
    setInitial(start);
    setForm(start);
    setErrors({});
    setSubmitted(false);
  }, [isOpen, group]);

  const change = (next: GroupForm) => {
    setForm(next);
    // why: show errors only after the first save attempt, then keep them current.
    if (submitted) setErrors(validateGroupForm(next, tg));
  };

  const updatePeriod = (key: number, patch: Partial<PeriodForm>) =>
    change({ ...form, periods: form.periods.map((p) => (p.key === key ? { ...p, ...patch } : p)) });

  const isDirty = !groupFormsEqual(form, initial);

  const handleSave = async () => {
    setSubmitted(true);
    const next = validateGroupForm(form, tg);
    setErrors(next);
    if (hasGroupErrors(next)) return;
    const request = formToRequest(form);
    const saved = group?.timeClockGroupId
      ? await updateGroup(group.timeClockGroupId, request)
      : await createGroup(request);
    if (saved) onClose();
  };

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={group ? tg('dialog.titleEdit') : tg('dialog.titleCreate')}
      size="lg"
      hasUnsavedChanges={isDirty}
      footer={
        <SlideOverFooter
          onCancel={onClose}
          onConfirm={() => void handleSave()}
          confirmText={t('common:actions.save')}
          cancelText={t('common:actions.cancel')}
          confirmDisabled={!isDirty && !!group}
          loading={saving}
        />
      }
    >
      <div className="space-y-5">
        <FormField
          label={tg('fields.name')}
          required
          value={form.name}
          maxLength={GROUP_LIMITS.maxNameLength}
          onChange={(e) => change({ ...form, name: e.target.value })}
          error={errors.name}
          placeholder={tg('placeholders.name')}
        />

        <FormField label={tg('fields.status')} helperText={tg('hints.isActive')}>
          <label className="flex items-center gap-2 mt-1 text-[13px] text-slate-700">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => change({ ...form, isActive: e.target.checked })}
              className="rounded border-slate-300"
            />
            {tg('fields.isActive')}
          </label>
        </FormField>

        <div className="space-y-3">
          <span className="block text-[12px] font-medium text-slate-600">{tg('fields.periods')}</span>
          {form.periods.map((period) => (
            <PeriodRow
              key={period.key}
              period={period}
              monthName={longMonth}
              windowError={errors.periodWindow?.[period.key]}
              overlapError={errors.periodOverlap?.[period.key]}
              canRemove={form.periods.length > 1}
              onChange={(patch) => updatePeriod(period.key, patch)}
              onRemove={() =>
                change({ ...form, periods: form.periods.filter((p) => p.key !== period.key) })
              }
            />
          ))}
          {errors.periods && <p className="text-[11px] text-red-600">{errors.periods}</p>}
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => change({ ...form, periods: [...form.periods, newPeriod()] })}
              disabled={form.periods.length >= GROUP_LIMITS.maxPeriods}
              leftIcon={<Plus className="w-3.5 h-3.5" strokeWidth={2} />}
            >
              {tg('actions.addPeriod')}
            </Button>
            <span className="text-[11px] text-slate-500">{tg('hints.periods')}</span>
          </div>
        </div>
      </div>
    </SlideOver>
  );
}

function PeriodRow({
  period,
  monthName,
  windowError,
  overlapError,
  canRemove,
  onChange,
  onRemove,
}: {
  period: PeriodForm;
  monthName: (month: number) => string;
  windowError?: string;
  overlapError?: string;
  canRemove: boolean;
  onChange: (patch: Partial<PeriodForm>) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation('timeClockGroups');

  const select = (
    label: string,
    options: number[],
    value: number,
    onSelect: (value: number) => void,
    format: (value: number) => string,
    hasError: boolean,
  ) => (
    <label className="space-y-1 block">
      <span className="block text-[11px] text-slate-500">{label}</span>
      <select
        value={value}
        onChange={(e) => onSelect(Number(e.target.value))}
        className={formFieldInputClassName(hasError, 'tabular-nums')}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {format(option)}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="rounded-md border border-slate-200 p-3 space-y-3">
      <div className="flex items-end gap-2">
        <div className="grid grid-cols-2 gap-3 flex-1">
          {select(t('period.fromMonth'), MONTHS, period.fromMonth, (v) => onChange({ fromMonth: v }), monthName, !!overlapError)}
          {select(t('period.toMonth'), MONTHS, period.toMonth, (v) => onChange({ toMonth: v }), monthName, !!overlapError)}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          disabled={!canRemove}
          title={t('period.remove')}
          aria-label={t('period.remove')}
          className="p-1 sm:p-2"
        >
          <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-600" strokeWidth={2} />
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {select(t('period.stampingStartHour'), START_HOURS, period.stampingStartHour, (v) => onChange({ stampingStartHour: v }), formatWindowHour, !!windowError)}
        {select(t('period.stampingEndHour'), END_HOURS, period.stampingEndHour, (v) => onChange({ stampingEndHour: v }), formatWindowHour, !!windowError)}
        {select(t('period.workCountStartHour'), START_HOURS, period.workCountStartHour, (v) => onChange({ workCountStartHour: v }), formatWindowHour, !!windowError)}
      </div>
      {overlapError && <p className="text-[11px] text-red-600">{overlapError}</p>}
      {windowError && <p className="text-[11px] text-red-600">{windowError}</p>}
    </div>
  );
}
